import { AsyncPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  output,
  signal,
  ViewEncapsulation,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDivider } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { RouterModule } from '@angular/router';

// third-party dependencies
import {
  CasualTournamentEntity,
  EventSchedule,
  NextEvent,
} from '@fantaz/shared/data-access/models';
import { PrizeImagePipe } from '@fantaz/shared/pipes';
import { SharedPipesDateModule } from '@fantaz/shared/pipes/date';
import { CurrencyModule } from '@fantaz/shared/ui/currency';
import { TournamentUrlPipe } from '@fantaz/tournaments/data-access';
import { PlayButtonModule } from '@fantaz/tournaments/ui/play-button';
import {
  CardDetails,
  CardDetailsActions,
  CardDetailsExpansionItem,
  CardDetailsItem,
  CardDetailsRowBody,
  CardDetailsRowDescription,
  CardDetailsRowTitle,
} from '@fantaz/ui';

@Component({
  selector: 'fz-mobile-card-details',
  standalone: true,
  imports: [
    AsyncPipe,
    CardDetails,
    CardDetailsActions,
    CardDetailsExpansionItem,
    CardDetailsItem,
    CardDetailsRowBody,
    CardDetailsRowDescription,
    CardDetailsRowTitle,
    CurrencyModule,
    MatButtonModule,
    MatDivider,
    MatIconModule,
    MatTooltip,
    PlayButtonModule,
    RouterModule,
    SharedPipesDateModule,
    TournamentUrlPipe,
    PrizeImagePipe,
  ],
  templateUrl: './mobile-card-details.html',
  styleUrls: ['./mobile-card-details.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class MobileCardDetails {
  /**
   * The tournament data
   *
   * @type {CasualTournamentEntity & { toPlayNow?: NextEvent }}
   */
  tournament = input.required<
    CasualTournamentEntity & { toPlayNow?: NextEvent }
  >();

  /**
   * Enabled or disabled the "To Play Now" section
   *
   * @default false
   * @type {boolean}
   */
  includeToPlayNow = input<boolean>(false);

  /**
   * Output event that emits the event schedule when the share button is clikced
   *
   * @type {EventSchedule}
   */
  share = output<void>();

  /**
   * A computed property that returns the schedule of the tournament. For all
   * tournaments, the schedule is the first event in the tournament's event
   * schedules. If there are no event schedules, it returns null.
   *
   * @returns {EventSchedule | null} The first event schedule or null if there are no event schedules.
   */
  schedule = computed<EventSchedule | null>(() => {
    const tournament = this.tournament();

    return tournament.eventSchedules.length > 0
      ? tournament.eventSchedules[0]
      : null;
  });

  /**
   * A computed property that returns the total number of seats available for a
   * tournament. It calculates the value based on the `schedule` property.
   *
   * @returns {number | null} The total number of seats if the schedule is
   * available, otherwise null.
   */
  seats = computed<number | null>(() => {
    const schedule = this.schedule();

    if (schedule) {
      return schedule.totalEntryCount || schedule.seatsPerInstance || null;
    }

    return null;
  });

  /**
   * A computed property that returns the prize value for the first place.
   * It retrieves the schedule and checks if prize details are available.
   * If prize details are found, it searches for the prize value associated with
   * the first rank.
   *
   * @returns {number | undefined} The prize value for the first place, or
   * undefined if not available.
   */
  firstPlacePrize = computed(() => {
    const schedule = this.schedule();

    if (schedule?.prizeDetails) {
      const prizeDetails = schedule.prizeDetails.find((el) => el.rank === 1);

      return prizeDetails?.prizeValue;
    }

    return undefined;
  });

  /**
   * zboReferral
   *
   * A computed property that calculates the referral amount per user for the
   * first level of the schedule's uni-level details.
   *
   * @returns {number | undefined} The amount per user for the first level if it
   * exists, otherwise undefined.
   */
  zboReferral = computed(() => {
    const schedule = this.schedule();

    if (schedule?.uniLevelDetails) {
      const dist = schedule.uniLevelDetails.find((el) => el.level === 1);

      if (dist) {
        return dist.amountPerUser;
      }
    }

    return undefined;
  });

  isOpened = signal<boolean>(false);

  /**
   * Toggles the state of if opened or not for the play now section item .
   *
   * @param isOpened - A boolean indicating whether the "Play Now" section should
   * be opened (true) or closed (false).
   */
  togglePlayNowExpended(isOpened: boolean) {
    this.isOpened.set(isOpened);
  }

  /**
   * Emits an event to share the tournament details.
   * This method triggers the `share` event emitter.
   */
  shareTournament() {
    this.share.emit();
  }
}
